import React from 'react';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Unhandled render error:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: '1rem', padding: '2rem', background: 'var(--bg, #f8fafc)',
        }}>
          <div style={{ fontSize: '3rem' }}>⚠️</div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Something went wrong</h2>
          <p style={{ margin: 0, color: 'var(--text-muted, #64748b)', maxWidth: 480, textAlign: 'center', fontSize: '0.9rem' }}>
            {this.state.error?.message ?? 'An unexpected error occurred.'}
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button
              className="btn btn-primary"
              onClick={() => this.setState({ hasError: false, error: undefined })}
            >
              Try again
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => window.location.reload()}
            >
              Reload page
            </button>
          </div>
          {import.meta.env.DEV && (
            <pre style={{
              marginTop: '1rem', padding: '1rem', background: '#1e293b', color: '#f1f5f9',
              borderRadius: 6, fontSize: '0.75rem', maxWidth: 600, overflowX: 'auto', whiteSpace: 'pre-wrap',
            }}>
              {this.state.error?.stack}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
